import React from 'react';
import { useLocation } from 'react-router-dom';
import Icon from '../AppIcon';

const WizardProgressIndicator = () => {
  const location = useLocation();

  const wizardSteps = [
    {
      id: 1,
      label: 'Welcome',
      path: '/welcome-calculator-introduction',
      icon: 'Home',
      description: 'Introduction to calculator'
    },
    {
      id: 2,
      label: 'Material Input',
      path: '/material-input-wizard',
      icon: 'Package',
      description: 'Enter material specifications'
    },
    {
      id: 3,
      label: 'Processing',
      path: '/calculation-processing',
      icon: 'Cog',
      description: 'Calculate processing costs'
    },
    {
      id: 4,
      label: 'Results',
      path: '/results-dashboard',
      icon: 'BarChart3',
      description: 'Review calculation results'
    },
    {
      id: 5,
      label: 'Export',
      path: '/export-summary',
      icon: 'Download',
      description: 'Export and share results'
    }
  ];

  const getCurrentStepIndex = () => {
    const currentStep = wizardSteps.find(step => step.path === location.pathname);
    return currentStep ? currentStep.id - 1 : 0;
  };

  const currentStepIndex = getCurrentStepIndex();

  const getStepStatus = (stepIndex) => {
    if (stepIndex < currentStepIndex) return 'completed';
    if (stepIndex === currentStepIndex) return 'current';
    return 'upcoming';
  };

  const getStepClasses = (status) => {
    switch (status) {
      case 'completed':
        return {
          container: 'text-success',
          circle: 'bg-success border-success text-white',
          line: 'bg-success'
        };
      case 'current':
        return {
          container: 'text-primary',
          circle: 'bg-primary border-primary text-white ring-4 ring-primary-100',
          line: 'bg-border'
        };
      default:
        return {
          container: 'text-text-tertiary',
          circle: 'bg-surface border-border text-text-tertiary',
          line: 'bg-border'
        };
    }
  };

  return (
    <div className="sticky top-0 z-40 bg-surface border-b border-border shadow-elevation-1">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
        {/* Desktop Progress Indicator */}
        <div className="hidden md:block">
          <nav aria-label="Progress">
            <ol className="flex items-center justify-between">
              {wizardSteps.map((step, stepIndex) => {
                const status = getStepStatus(stepIndex);
                const classes = getStepClasses(status);
                const isLast = stepIndex === wizardSteps.length - 1;

                return (
                  <li key={step.id} className="relative flex-1">
                    <div className="flex items-center">
                      {/* Step Circle */}
                      <div className="relative flex items-center justify-center">
                        <div
                          className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${classes.circle}`}
                          aria-current={status === 'current' ? 'step' : undefined}
                        >
                          {status === 'completed' ? (
                            <Icon name="Check" size={18} />
                          ) : (
                            <Icon name={step.icon} size={18} />
                          )}
                        </div>
                      </div>

                      {/* Step Content */}
                      <div className="ml-2 min-w-0 flex-1">
                        <div className={`text-sm font-body-medium ${classes.container}`}>
                          {step.label}
                        </div>
                      </div>

                      {/* Connecting Line removed */}
                      {/* {!isLast && (
                        <div className="flex-1 ml-2">
                          <div className={`h-0.5 transition-all duration-300 ${classes.line}`} />
                        </div>
                      )} */}
                    </div>
                  </li>
                );
              })}
            </ol>
          </nav>
        </div>

        {/* Mobile Progress Indicator */}
        <div className="md:hidden">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className={`w-7 h-7 rounded-full border-2 flex items-center justify-center ${getStepClasses(getStepStatus(currentStepIndex)).circle}`}>
                {getStepStatus(currentStepIndex) === 'completed' ? (
                  <Icon name="Check" size={14} />
                ) : (
                  <Icon name={wizardSteps[currentStepIndex].icon} size={14} />
                )}
              </div>
              <div>
                <div className="text-sm font-body-medium text-text-primary">
                  {wizardSteps[currentStepIndex].label}
                </div>
                <div className="text-xs text-text-secondary">
                  Step {currentStepIndex + 1} of {wizardSteps.length}
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="flex-1 ml-2">
              <div className="bg-border rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${((currentStepIndex + 1) / wizardSteps.length) * 100}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WizardProgressIndicator;